import { CustomerSatisfaction } from '@/modules/Ticket/types/response'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import StarIcons from '@/icons/StarIcons'

interface CustomerSatisfactionChartProps {
    customerSatisfaction: CustomerSatisfaction | null
}

const CustomerSatisfactionChart: React.FC<CustomerSatisfactionChartProps> = ({
    customerSatisfaction,
}) => {
    const { t } = useTranslation()

    const renderStars = (count: number, size: 'sm' | 'md' = 'md') => {
        const sizeClass = size === 'sm' ? 'w-3 h-3' : 'w-5 h-5'
        return Array.from({ length: 5 }, (_, i) => (
            <StarIcons
                key={i}
                className={`${sizeClass} ${i < count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`}
            />
        ))
    }

    const ratingData = customerSatisfaction
        ? [
              { stars: 5, count: customerSatisfaction.five_star },
              { stars: 4, count: customerSatisfaction.four_star },
              { stars: 3, count: customerSatisfaction.three_star },
              { stars: 2, count: customerSatisfaction.two_star },
              { stars: 1, count: customerSatisfaction.one_star },
          ]
        : []

    const maxCount = Math.max(...ratingData.map((r) => r.count), 1)
    const totalRated = customerSatisfaction?.total_rated || 0

    const getRatingColor = (rating: number) => {
        if (rating >= 4) return 'text-green-600'
        if (rating >= 3) return 'text-yellow-600'
        return 'text-red-600'
    }

    return (
        <div className="bg-white rounded-[8px] p-4 lg:col-span-4 md:col-span-1 col-span-1 half-col-print">
            <Typogaphy size="small" className="text-primary font-semibold">
                {t('ticket-customer-satisfaction')}
            </Typogaphy>

            <div className="min-h-[280px] flex flex-col justify-center">
                {!customerSatisfaction || totalRated === 0 ? (
                    <Typogaphy className="text-gray-500 text-center text-sm">
                        {t('no-data')}
                    </Typogaphy>
                ) : (
                    <>
                        {/* Average Rating Display */}
                        <div className="text-center mb-5">
                            <div
                                className={`text-5xl font-bold ${getRatingColor(customerSatisfaction.avg_rating || 0)}`}
                            >
                                {customerSatisfaction.avg_rating?.toFixed(1) ||
                                    '0.0'}
                            </div>
                            <div className="flex justify-center gap-0.5 mt-2">
                                {renderStars(
                                    Math.round(
                                        customerSatisfaction.avg_rating || 0
                                    )
                                )}
                            </div>
                            <div className="text-sm text-gray-500 mt-2">
                                {totalRated} {t('feedback-total-reviews')}
                            </div>
                        </div>

                        {/* Rating Breakdown */}
                        <div className="space-y-2.5">
                            {ratingData.map((item) => (
                                <div
                                    key={item.stars}
                                    className="flex items-center gap-3 text-sm"
                                >
                                    <div className="flex items-center gap-1 w-16">
                                        <span className="font-medium text-gray-700 w-3">
                                            {item.stars}
                                        </span>
                                        <StarIcons className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                    </div>
                                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full transition-all duration-500"
                                            style={{
                                                width: `${(item.count / maxCount) * 100}%`,
                                            }}
                                        />
                                    </div>
                                    <span className="w-8 text-right text-gray-600 font-medium">
                                        {item.count}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default CustomerSatisfactionChart
